import router from './router'
import store from './store'
import { jwtDecode } from 'jwt-decode'
import FqApi from '@/services/fq-api'
import NavService from './components/NavService'
import { setSentryUserContext, clearSentryUserContext } from '@/services/sentryUserContext'
import { isJwtLoginEnabled } from '@/services/featureFlags'
import { goToLogin } from '@/views/auth/loginNavigation'
import { returnUrlFor } from '@/views/auth/returnUrl'

const TOKEN_COOKIE = 'UniqApi.Token'
// Set by the WCF sign-in, never by the JWT path - see hasLegacySessionCookie.
const LEGACY_SESSION_COOKIE = 'uniq.sessionid'
const SUPPORT_USER_KEY = 'UniqApi.IsSupportUser'
const FALLBACK_MAX_AGE_SECONDS = 2 * 24 * 60 * 60 // mirrors WebTokenExpirationInMinutes

/**
 * Seconds until the JWT's own `exp`, so the cookie cannot outlive its token or drift when the
 * server-side lifetime changes. Falls back to the server default if the token is unreadable.
 */
function tokenMaxAgeSeconds(jwt) {
  try {
    const { exp } = jwtDecode(jwt)
    if (!exp) return FALLBACK_MAX_AGE_SECONDS
    const seconds = Math.floor(exp - Date.now() / 1000)
    return seconds > 0 ? seconds : FALLBACK_MAX_AGE_SECONDS
  } catch (e) {
    return FALLBACK_MAX_AGE_SECONDS
  }
}

/**
 * The token cannot be HttpOnly - the request interceptor reads it from document.cookie - so it
 * stays XSS-readable. SameSite and Secure are the only attributes available to us.
 */
function cookieAttributes() {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  return `; path=/; SameSite=Lax${secure}`
}

function setTokenCookie(jwt) {
  document.cookie = `${TOKEN_COOKIE}=${jwt}${cookieAttributes()}; max-age=${tokenMaxAgeSeconds(jwt)}`
}

function clearTokenCookie() {
  document.cookie = `${TOKEN_COOKIE}=${cookieAttributes()}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

function getTokenCookie() {
  const entry = document.cookie.split('; ').find(c => c.startsWith(`${TOKEN_COOKIE}=`))
  return entry ? entry.slice(TOKEN_COOKIE.length + 1) : ''
}

/**
 * True when this session came from SignIn.aspx, which sets `uniq.sessionid` alongside the
 * FormsAuth ticket. The JWT path sets neither, so this distinguishes a hybrid session - the
 * only case where the legacy WCF sign-out still has anything to do.
 */
function hasLegacySessionCookie() {
  return document.cookie.split('; ').some(c => c.startsWith(`${LEGACY_SESSION_COOKIE}=`) &&
    c.length > LEGACY_SESSION_COOKIE.length + 1)
}

/**
 * Remembered so an interceptor-driven logout, which has no caller to tell it who signed in,
 * still returns support users to /login-csr.
 */
function rememberIsSupportUser(isSupportUser) {
  try {
    localStorage.setItem(SUPPORT_USER_KEY, isSupportUser ? 'true' : 'false')
  } catch (e) { /* private mode / quota - fall back to the store */ }
}

function recalledIsSupportUser() {
  try {
    return localStorage.getItem(SUPPORT_USER_KEY) === 'true'
  } catch (e) {
    return false
  }
}

/**
 * Auth Plugin
 *
 * (see https://vuejs.org/v2/guide/plugins.html for more info on Vue.js plugins)
 *
 * Handles login and token authentication using OAuth2.
 */
export default {
  /**
   * Install the Auth class.
   *
   * Creates a Axios http interceptor to handle automatically adding auth headers
   * and refreshing tokens. Then attaches this object to the global Vue (as Vue.auth).
   *
   * @param {Object} Vue The global Vue.
   * @param {Object} options Any options we want to have in our plugin.
   * @return {void}
   */
  install(Vue, options) {
    Vue.prototype.$auth = Vue.auth = this
  },

  isLoggedIn() {
    return FqApi.get('/auth/isLoggedIn')
      .then(response => {
        return true
      })
      .catch(err => {
        console.log(err)
        return false
      })
  },

  /**
   * Login
   *
   * Returns the JWT and sets the auth cookie. Navigation is the caller's job and must be a
   * full page load, not a router.push: App.vue runs its bootstrap once on router.onReady, so
   * an in-app transition would render the shell with no user, company or permissions loaded.
   *
   * @param {Object.<string>} creds companyLoginId, username and password.
   * @return {Promise<string>} the JWT.
   */
  login(creds) {
    return FqApi.post('/auth/web/signin', creds)
      .then(response => {
        const result = (response && response.data) || {}

        // An account still on its admin-assigned password gets a ticket instead of a token, and
        // cannot reach the app until it has been changed. Nothing is stored in that case.
        if (result.requiresPasswordChange) {
          return { requiresPasswordChange: true, ticket: result.ticket }
        }

        setTokenCookie(result.token)
        rememberIsSupportUser(false)
        setSentryUserContext()
        return { token: result.token }
      })
  },

  /**
   * Logout
   *
   * Hits the WebAPI to delete the UserSession row, then clears the cookie and
   * full-navigates to the right login route. Callers with access to user info
   * should pass `{ isSupportUser }` so support users land on /login-csr.
   *
   * @param {{ isSupportUser?: boolean }} [options]
   * @return {Promise<void>}
   */
  async logout(options) {
    clearSentryUserContext()

    // Caller value, then the store, then what was remembered at sign-in - interceptor-driven
    // logouts pass no options and may run after the store is gone.
    const storedUser = store.getters['userInfo/user']
    const isSupportUser = options && typeof options.isSupportUser === 'boolean'
      ? options.isSupportUser
      : (storedUser && storedUser.info && typeof storedUser.info.isSupportUser === 'boolean')
        ? storedUser.info.isSupportUser
        : recalledIsSupportUser()

    // Both captured before any cookie is cleared, since the calls below depend on them.
    const token = getTokenCookie()
    const isHybridSession = hasLegacySessionCookie()

    // Clear local credentials FIRST. If a network call below hangs, the user must still end up
    // signed out locally rather than sitting on a live cookie.
    clearTokenCookie()

    // Invalidates the JWT immediately rather than at exp. Runs regardless of the feature flag,
    // and web/signout is ungated to match: the legacy WCF SignOut deletes by SSID and so never
    // matches a JWT-path session, which would leave the row behind. Authorization is explicit
    // because the cookie is already cleared.
    if (token) {
      try {
        await FqApi.post('/auth/web/signout', null, {
          headers: { Authorization: `Bearer ${token}` }
        })
      } catch (e) { /* best effort */ }
    }

    // Only for a hybrid session - signed in through SignIn.aspx, so there is a FormsAuth ticket
    // and an ASP.NET session that only the WebForms side can clear. A JWT-path session has
    // neither, and the WCF call would be a no-op against a synthetic SSID. Skipping it there is
    // what leaves the SPA with no WCF dependency at all.
    //
    // Awaited: it returns a promise, so a bare try/catch would miss the rejection, and the
    // navigation below would otherwise abort it in flight.
    if (isHybridSession) {
      try { await NavService.signOut() } catch (e) { /* best effort */ }
    }

    // Only on the JWT path - the WebForms login does not consume a returnUrl - and never the
    // login route itself, which would bounce the user straight back after signing in.
    const current = router.currentRoute
    const onAuthRoute = current && current.matched.some(r => r.meta && r.meta.bypassAuth)
    const returnUrl = (await isJwtLoginEnabled()) && !onAuthRoute
      ? returnUrlFor(current)
      : undefined

    // goToLogin resolves the flag itself to pick the Vue route or the WebForms page.
    return goToLogin({ isSupportUser, returnUrl })
  },

  /**
   * CSR (support-user) Sign-In — multi-step flow.
   *
   * Backs the /login-csr Vue page. Mirrors the legacy SignInCsr.aspx JS calls but talks
   * to the new WebAPI endpoints under /v1/auth/web/csr/ instead of the WCF services.
   */
  csr: {
    /**
     * Step 1. On success the response carries a short-lived, single-use `ticket` that every
     * later step must present. The ticket, not a user id, is what identifies the caller —
     * a user id is guessable and would let anyone drive the rest of this flow.
     */
    validateCredential(creds) {
      return FqApi.post('/auth/web/csr/validate-credential', creds).then(r => r.data)
    },
    getCompanies(ticket) {
      return FqApi.post('/auth/web/csr/companies', { ticket }).then(r => r.data)
    },
    // No session-in-use / reset-session step: the legacy CSR page never reached its own
    // version (SignInSupport goes straight to sign-in, and CheckUserSessionInUse is dead
    // code reading an element that page does not have). Signing in upserts the session row,
    // which is exactly what legacy did.
    /** Consumes the ticket. A fresh validateCredential is required to retry. */
    signIn(payload) {
      return FqApi.post('/auth/web/csr/signin', payload).then(r => {
        const jwt = r.data
        setTokenCookie(jwt)
        rememberIsSupportUser(true)
        setSentryUserContext()
        return jwt
      })
    }
  },

  /**
   * First-time-login password change (IsNewAccount). Ticket-gated and restricted server-side
   * to accounts still flagged ACCOUNT_CREATED, so this is not a general password-change route.
   * Consumes the ticket — the caller re-runs validateCredential with the new password to
   * obtain a fresh one.
   */
  changePassword(payload) {
    return FqApi.post('/auth/web/change-password', payload).then(r => r.data)
  }
}
